/**
 * wipPlan — whether a WIP is a full set, a top only, or pants only.
 *
 * WHY PAYROLL CARES. Every set is paid twice, a design band for the top and a
 * PANTS band for the trousers (payoutDraft.ts, established against the 7 Sep
 * 2026 payslips). That is right for a set and wrong for half of one, in both
 * directions:
 *
 *   TOP ONLY    pays a pants band for trousers nobody sewed.
 *   PANTS ONLY  pays a design band for a top nobody sewed.
 *
 * Mikko, 2026-09-13, with WO-260911-B on screen: WIP 26090321 is TOP ONLY at
 * S and 26090322 is PANTS ONLY at M, same store, same serial 46536-2, same
 * SS032 in midnight blue. One customer ordering a small top and medium pants,
 * cut as two WIPs. Priced as two full sets that order pays four bands and owes
 * two.
 *
 * WHERE THE ANSWER COMES FROM. The Cutting J.O. Log decides it at cutting,
 * from the MASTER FILE's REMARKS, and `joPlan` has read it since the WO Log
 * screen existed — TOP ONLY and PANTS ONLY, misspellings included. Every
 * imported log saves that per row in `iSyn2_WoLogBatches`, shared, and the
 * Control Tower pencil can correct it. So payroll does not need the fact
 * carried down through BQC3, BQC4 and BQC5: four copies of one fact can
 * disagree and then nobody can say which is right. Every station row already
 * carries the WIP id, so this is a lookup, not a delivery.
 *
 * AND WHERE IT DOES NOT. Importing WO Logs here began 4 September 2026;
 * everything before that reached the app through the hub, whose BQC1 sheet has
 * no REMARKS column at all. Those WIPs have no plan anywhere in the system, so
 * a person picks one on the run screen and it is remembered — the same shape
 * as the sewer alias picker, and for the same reason: never guess a fact that
 * decides money, ask once and keep the answer.
 *
 * THE ORDER IS PERSON, THEN THE SKU'S OWN -TO-/-PO- MARKER, THEN THE LOG, THEN
 * ASSUMPTION. A stored choice wins
 * over the log because it was made by somebody looking at this WIP. An
 * assumption of BOTH is what the run did before any of this existed, so it
 * changes no figure that was already right — but it is COUNTED and shown,
 * because a silent assumption is how the overpay stayed invisible.
 *
 * Pure and I/O-free.
 */
import type { TagPlan } from "../mfg-workcenters/bqc1/prodTag";
import type { IWoLogBatch } from "../mfg-workcenters/bqc1/woLogStorage";
import { planOfSku } from "../mfg-workcenters/bqc4/grnMix";

export type { TagPlan };

/** The settings-list key the stored choices live under. */
export const WIP_PLANS_KEY = "wipPlans";

/** Where a WIP's plan came from — shown beside it, never inferred twice. */
export type PlanSource =
  /** Somebody chose it on the run screen. */
  | "chosen"
  /** The SKU itself says so: -TO- (top only) or -PO- (pants only) before the size. */
  | "sku"
  /** The Cutting J.O. Log said so, via REMARKS. */
  | "wo-log"
  /** Nothing said; a full set is assumed, and the run counts it. */
  | "assumed";

export interface IResolvedPlan {
  plan: TagPlan;
  source: PlanSource;
}

/** WIP id → the plan a person chose, as stored in the settings list. */
export type StoredWipPlans = Record<string, TagPlan>;

export const PLAN_LABEL: Record<TagPlan, string> = {
  BOTH: "Set",
  TOP: "Top only",
  PANTS: "Pants only",
};

export const PLAN_SOURCE_LABEL: Record<PlanSource, string> = {
  chosen: "Chosen here",
  sku: "From the SKU (-TO- / -PO-)",
  "wo-log": "From the WO Log",
  assumed: "Assumed a set",
};

/** The three choices the run screen offers, in the order it offers them. */
export const PLAN_CHOICES: readonly { v: TagPlan; label: string }[] = [
  { v: "BOTH", label: PLAN_LABEL.BOTH },
  { v: "TOP", label: PLAN_LABEL.TOP },
  { v: "PANTS", label: PLAN_LABEL.PANTS },
];

const PLANS = new Set<string>(["BOTH", "TOP", "PANTS"]);

/** A plan from anything, or null. Accepts the wordings a person types as well
 *  as the stored codes, so "top only" and "TOP" mean the same. */
export function asPlan(v: unknown): TagPlan | null {
  const s = String(v ?? "").toUpperCase().replace(/[^A-Z]+/g, " ").trim();
  if (!s) return null;
  if (PLANS.has(s)) return s as TagPlan;
  if (/^TOPS? ONLY$/.test(s)) return "TOP";
  if (/^PANTS? ONLY$/.test(s)) return "PANTS";
  if (s === "SET" || s === "BOTH") return "BOTH";
  return null;
}

/** The stored choices, from the settings list's JSON. Anything unreadable is
 *  no choices at all — the WO Log then decides, which is the safe direction. */
export function parseStoredWipPlans(text: string | null | undefined): StoredWipPlans {
  if (!text) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out: StoredWipPlans = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const wipId = String(k ?? "").trim();
      const plan = asPlan(v);
      if (wipId && plan) out[wipId] = plan;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * WIP id → plan, from every imported Cutting J.O. Log.
 *
 * A WIP re-issued on a later log wins: the newest import is the current
 * instruction, and the older batch is history somebody kept for reprinting.
 * Ordered by import time rather than by list order, because the batches come
 * back in whatever order SharePoint hands them over.
 */
export function planByWipFromBatches(batches: readonly IWoLogBatch[]): Map<string, TagPlan> {
  const at = new Map<string, string>();
  const out = new Map<string, TagPlan>();
  for (const b of batches) {
    const stamp = String(b.importedAt ?? "");
    for (const r of b.rows ?? []) {
      const wipId = String(r.wipId ?? "").trim();
      if (!wipId) continue;
      const plan = asPlan(r.plan) ?? "BOTH";
      const seen = at.get(wipId);
      if (seen !== undefined && seen > stamp) continue;
      at.set(wipId, stamp);
      out.set(wipId, plan);
    }
  }
  return out;
}

export interface IPlanSources {
  /** What a person chose on the run screen. Wins. */
  chosen?: StoredWipPlans;
  /** What the Cutting J.O. Log said. */
  woLog?: ReadonlyMap<string, TagPlan>;
}

/** One WIP's plan and where it came from. Never throws, never guesses
 *  silently: an unanswered WIP is "assumed", which the run counts. */
export function resolveWipPlan(wipId: string, sources: IPlanSources = {}, sku?: string | null): IResolvedPlan {
  const id = String(wipId ?? "").trim();
  if (id) {
    const chosen = sources.chosen ? asPlan(sources.chosen[id]) : null;
    if (chosen) return { plan: chosen, source: "chosen" };
  }
  // THE GARMENT'S OWN SKU (Mikko, 2026-10-03: SS032-BLK-TO-L "should be
  // automatic considered as either Top Only or Pants Only"). The marker is on
  // the garment that was dispatched and passed, so it needs no log and no
  // person: it ranks below a person's choice, above the J.O. Log, and the Remarks
  // column already reads it from the same place (payrollGrn.remarkOf).
  const marked = planOfSku(sku);
  if (marked === "TOP" || marked === "PANTS") return { plan: marked, source: "sku" };
  if (id) {
    const wo = sources.woLog?.get(id);
    if (wo) return { plan: wo, source: "wo-log" };
  }
  return { plan: "BOTH", source: "assumed" };
}

/** True when this plan earns the design (top) band. */
export const paysTop = (plan: TagPlan): boolean => plan !== "PANTS";

/** True when this plan earns the PANTS band. */
export const paysPants = (plan: TagPlan): boolean => plan !== "TOP";
